'use client';

import { c as _c } from "react/compiler-runtime";
import type { ColumnDef } from '@tanstack/react-table';
import { InterfaceChainUtils, parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CellTokenPrice, NetworkAvatar as Network, VaultCell } from '@morpho-org/ui-v6/client';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { columnFilteringFeature, columnSizingFeature, columnVisibilityFeature, createSortedRowModel, rowSortingFeature, sortFns, tableFeatures } from '@tanstack/react-table';
import CellNetApy from '@/components/common/Cells/CellNetApy';
import RiskCuratorCell from '@/components/common/Cells/RiskCuratorCell';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { VaultPositionManager as VaultPositionManagerClient } from '@/components/common/PositionManager/VaultPositionManager';
import { VaultV2PositionManager as VaultV2PositionManagerClient } from '@/components/common/PositionManager/VaultV2PositionManager';
import { UpgradeButton, useV1ToV2MigrationTarget } from '@/components/common/V1ToV2Migration';
import { OwnPortfolioOnly } from '@/components/DashboardPage/OwnPortfolioOnly';
import { APY_DURATION_LABEL } from '@/constants/avgApy';
import type { DashboardEarnPositionsTableRow } from './DashboardEarnPositionsTable.types';
import { COLUMN_IDS } from './DashboardEarnPositionsTable.config';
export const COLUMN_LABELS = {
  [COLUMN_IDS.Network]: 'Network',
  [COLUMN_IDS.Vault]: 'Vault',
  [COLUMN_IDS.Assets]: 'Deposits',
  [COLUMN_IDS.NetApy]: 'APY',
  [COLUMN_IDS.RiskCurator]: 'Curator',
  [COLUMN_IDS.CollateralExposures]: 'Exposure',
  [COLUMN_IDS.RealizedEarnings]: 'Earned Interest'
};
const DashboardEarnPositionManagerCell = t0 => {
  const $ = _c(11);
  const {
    row
  } = t0;
  const chainId = row.vault.asset.chain.id;
  const migrationTarget = useV1ToV2MigrationTarget(row.isVaultV2 ? undefined : row.vault.address, chainId);
  if (row.vault.isGated) {
    return null;
  }
  let t1;
  if ($[0] !== chainId || $[1] !== row.isVaultV2 || $[2] !== row.vault.address || $[3] !== row.vault.asset.address || $[4] !== row.vault.whitelisted) {
    t1 = row.isVaultV2 ? <VaultV2PositionManagerClient chainId={chainId} vaultAddress={row.vault.address} assetAddress={row.vault.asset.address} enableDeposit={row.vault.whitelisted} /> : <VaultPositionManagerClient chainId={chainId} vaultAddress={row.vault.address} assetAddress={row.vault.asset.address} enableDeposit={row.vault.whitelisted} />;
    $[0] = chainId;
    $[1] = row.isVaultV2;
    $[2] = row.vault.address;
    $[3] = row.vault.asset.address;
    $[4] = row.vault.whitelisted;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const positionManager = t1;
  let t2;
  if ($[6] !== migrationTarget) {
    t2 = migrationTarget && <UpgradeButton migrationTarget={migrationTarget} />;
    $[6] = migrationTarget;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== positionManager || $[9] !== t2) {
    t3 = <div className="gap-xs flex items-center">{t2}{positionManager}</div>;
    $[8] = positionManager;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const earnPositionsTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  sortFns,
  sortedRowModel: createSortedRowModel()
});
export type EarnPositionsTableFeatures = typeof earnPositionsTableFeatures;
export const columnsEarnPositions: ColumnDef<EarnPositionsTableFeatures, DashboardEarnPositionsTableRow>[] = [{
  id: COLUMN_IDS.Network,
  minSize: 100,
  size: 104,
  accessorFn: row => InterfaceChainUtils.getMetadata(row.vault.asset.chain.id).chainIdentifier,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {COLUMN_LABELS[COLUMN_IDS.Network]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network of the vault.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    const chainId = row.original.vault.asset.chain.id;
    const chainName = InterfaceChainUtils.getMetadata(chainId).chain.name;
    return <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Network chainId={chainId} srcAvatarProps={{
            size: 'small'
          }} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{chainName}</TooltipContent>
        </Tooltip>;
  }
}, {
  id: COLUMN_IDS.Vault,
  size: 250,
  accessorFn: row => row.vault.name,
  header: () => <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS[COLUMN_IDS.Vault]}</span>,
  cell: ({
    row
  }) => {
    const vault = row.original.vault;
    return <VaultCell address={vault.address} name={vault.name} logoURI={vault.asset.logoURI} warnings={vault.warnings} chainId={vault.asset.chain.id} isVaultV2={row.original.isVaultV2} logoSize="small" />;
  }
}, {
  id: COLUMN_IDS.Assets,
  size: 150,
  accessorFn: row => row.assets.assetsUsd,
  enableColumnFilter: true,
  filterFn: (row, id, value) => {
    if (!value || value === '') return true;
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const depositValue = row.getValue(id) as bigint;
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const filterValue = value as string;
    const numericValue = parseFloat(filterValue);
    if (isNaN(numericValue) || numericValue < 0) return true;
    const depositNumeric = Number(depositValue) / Math.pow(10, 18);
    return depositNumeric >= numericValue;
  },
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '14px'
      }}>
            {COLUMN_LABELS[COLUMN_IDS.Assets]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total assets you have deposited in the vault.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <CellTokenPrice token={row.original.assets.asset} amount={row.original.assets.assets} value={row.original.assets.assetsUsd} />;
  }
}, {
  id: COLUMN_IDS.RiskCurator,
  size: 70,
  enableSorting: false,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '14px'
      }}>
            {COLUMN_LABELS[COLUMN_IDS.RiskCurator]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The address setting market exposure, supply caps, and risk parameters of the vault.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <RiskCuratorCell curators={row.original.vault.curators} chainId={null} logoSize="small" withLabel={false} />
}, {
  id: COLUMN_IDS.CollateralExposures,
  size: 110,
  enableSorting: false,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {COLUMN_LABELS[COLUMN_IDS.CollateralExposures]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The collateral asset of each market the vault is lending to.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    return <CollateralExposureStack maxTokensCount={5} collateralExposures={row.original.collateralExposures.collateralExposures} size="small" totalAssetsUsd={row.original.vault.totalAssetsUsd} />;
  }
}, {
  id: COLUMN_IDS.NetApy,
  // Fits the CellNetApy hover pill (value + up to 2 reward/points icons +
  // padding + cell indent) so it can't overflow into the next cell and get
  // clipped by its hover background.
  size: 125,
  accessorFn: row => row.netApy.netApy,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '14px'
      }}>
            {COLUMN_LABELS[COLUMN_IDS.NetApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`${APY_DURATION_LABEL} Native APY + Rewards - Fees`}</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    if (!isDefined(row.original.netApy.netApy)) return null;
    return <CellNetApy rewards={row.original.netApy.rewards} netApy={row.original.netApy.netApy} apy={row.original.netApy.apy} performanceFee={row.original.netApy.performanceFee} managementFee={row.original.netApy.managementFee} points={row.original.netApy.points} variant="vault" asset={parseApiAsset(row.original.vault.asset)} maxRate={row.original.netApy.maxRate} maxApy={row.original.netApy.maxApy} isRateCapped={row.original.netApy.isRateCapped} includesDonations={row.original.netApy.includesDonations} apyBreakdown={row.original.netApy.apyBreakdown} merklContext={{
      kind: 'vault',
      chainId: row.original.vault.asset.chain.id,
      identifier: row.original.vault.address
    }} />;
  }
}, {
  id: COLUMN_IDS.RealizedEarnings,
  size: 180,
  accessorFn: row => row.realizedEarnings?.usdValue,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{
        marginLeft: '14px'
      }}>
            {COLUMN_LABELS[COLUMN_IDS.RealizedEarnings]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Interest accrued to date on your active positions, excluding rewards.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <CellTokenPrice token={row.original.assets.asset} amount={row.original.realizedEarnings.assetValue} value={row.original.realizedEarnings.usdValue} withAvatar={true} />
}, {
  // Minimum width; the auto-layout table widens it to fit the Upgrade button when one renders.
  id: COLUMN_IDS.PositionManager,
  size: 56,
  header: () => null,
  cell: ({
    row
  }) => <OwnPortfolioOnly>
        <DashboardEarnPositionManagerCell row={row.original} />
      </OwnPortfolioOnly>
}];